import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Quill from 'quill';
import classes from './Shape.module.css';
import useResizer from '../../../../hooks/use-resizer';
import { useObjectAnimation } from '../../../Animations/hooks/AnimationContext';
import { resolvePreset, resolveAdjustments } from './geometry/presetRegistry';
import { useMeasuredSize } from './useMeasuredSize';
import { useShapePersistence } from './useShapePersistence';
import { TOOLBAR_CONFIG } from './quillSetup';
import ShapeGeometry from './ShapeGeometry';
import ShapeText from './ShapeText';
import ShapeAdjustHandles from './ShapeAdjustHandles';

export const Shape = ({
  shapeId,
  currentSlideIdProp = null,
  isPropertyPreviewMode = false,
  previewModeProperies = null,
}) => {
  // Outside a slide (property preview thumbnails) this resolves to the no-op context.
  const shapeAnim = useObjectAnimation(shapeId);

  const containerRef = useRef(null);
  const quillRef = useRef();
  const [addResizer, removeResizer, dragEvent] = useResizer();
  const [isSelected, setIsSelected] = useState(false);
  const [isEditingText, setIsEditingText] = useState(false);
  const [checkCurrentSlideId, setCurrentSlideId] = useState();

  const existingSlides = useSelector((state) => state.slide.slides);
  let currentSlideId = useSelector((state) => state.slide.currentSlideId);
  if (currentSlideIdProp) {
    currentSlideId = currentSlideIdProp;
  }

  const shapeData = useSelector(
    (state) => state.slide.slides[currentSlideId]?.shapes[shapeId]
  );
  const presentMode = useSelector((state) => state.slide.presentMode);
  const currentSlideData = existingSlides?.[currentSlideId];

  const { setShapeText } = useShapePersistence({
    shapeId,
    currentSlideId,
    quillRef,
    addResizer,
    removeResizer,
  });

  // --- Properties -----------------------------------------------------------
  // Resolved before the geometry below, which reads strokeWidth and adjustments.
  const currentSvgStyles =
    currentSlideData?.additionalData?.elementsData?.[shapeId]?.properties || {};
  const sourceStyles =
    isPropertyPreviewMode && previewModeProperies ? previewModeProperies : currentSvgStyles;

  const internalProperties = {
    isTransparent: sourceStyles.isTransparent ?? false,
    strokeWidth: sourceStyles.strokeWidth ?? 0.5,
    backgroundColor: sourceStyles.backgroundColor ?? '#fff',
    borderColor: sourceStyles.borderColor ?? '#000',
    strokeUnit: sourceStyles.strokeUnit ?? 'px',
    width: sourceStyles.width ?? shapeData?.styles?.width,
    height: sourceStyles.height ?? shapeData?.styles?.height,
    opacity: sourceStyles.opacity ?? 100,
    rotation: sourceStyles.rotation ?? 0,
    // Persisted by PropertiesModal but never read by the old Shape.js.
    borderStyle: sourceStyles.borderStyle ?? 'solid',
    flipH: sourceStyles.flipH ?? false,
    flipV: sourceStyles.flipV ?? false,
  };

  // --- Geometry -------------------------------------------------------------
  const size = useMeasuredSize(containerRef);
  const preset = useMemo(() => resolvePreset(shapeId), [shapeId]);

  const adjustments = useMemo(() => {
    const stored = { ...sourceStyles.adjustments };
    // borderRadius maps onto the rounded rectangle's adjust value, and only
    // there: PowerPoint treats corner rounding as roundRect geometry rather
    // than a universal border option.
    if (
      preset.name === 'roundRect' &&
      stored.adj === undefined &&
      sourceStyles.borderRadius !== undefined
    ) {
      const radius = parseFloat(sourceStyles.borderRadius);
      if (Number.isFinite(radius) && size.width > 0 && size.height > 0) {
        stored.adj = radius / Math.min(size.width, size.height);
      }
    }
    return resolveAdjustments(preset, stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, sourceStyles.adjustments, sourceStyles.borderRadius, size.width, size.height]);

  const stroke = Math.max(0, Number(internalProperties.strokeWidth) || 0);
  const box = {
    x: stroke / 2,
    y: stroke / 2,
    width: Math.max(0, size.width - stroke),
    height: Math.max(0, size.height - stroke),
  };
  const textRect = preset.textRect(box, adjustments);

  // --- Text editor ----------------------------------------------------------
  useEffect(() => {
    if ((currentSlideId && currentSlideId !== checkCurrentSlideId) || presentMode) {
      const globalToolbarContainer = document.querySelector('#quillToolbar');
      const quill = new Quill(`#quill${shapeId}`, {
        theme: 'snow',
        modules: { toolbar: TOOLBAR_CONFIG },
      });
      if (presentMode) {
        quill.container.firstChild.setAttribute('contenteditable', 'false');
        setCurrentSlideId(null);
      } else {
        setCurrentSlideId(currentSlideId);
      }
      quill.setContents(shapeData.delta);
      quillRef.current = quill;

      quill.on('text-change', function (delta, oldDelta, source) {
        if (source === 'listener') {
          quill.setSelection(null);
          return;
        }
        // Capitalise the first letter while the shape is still a single word.
        !quill.getText().includes(' ') &&
          quill.container.addEventListener(
            'keydown',
            function (e) {
              if (e.key === ' ' || e.key === 'enter') {
                const firstWord = quill.editor.delta.ops[0].insert.split('');
                firstWord[0] = firstWord[0].toUpperCase();
                quill.deleteText(0, firstWord.length - 1);
                quill.insertText(0, firstWord.join(''));
                quill.deleteText(firstWord.length - 1, 1);
              }
            },
            { once: true }
          );
        setShapeText({ content: quill.getContents(), shapeId });
      });

      quill.on('selection-change', function (range) {
        if (range) {
          globalToolbarContainer.appendChild(toolbar.container);
          toolbar.container.addEventListener(
            'mousedown',
            function (e) {
              e.preventDefault();
            },
            false
          );
        } else if (range === null || !quill.hasFocus) {
          globalToolbarContainer.children.length !== 0 &&
            globalToolbarContainer.removeChild(toolbar.container);
          // Target the shape by id rather than walking up from the Quill
          // container, so this keeps working regardless of the text nesting.
          removeResizer(document.getElementById(shapeId));
        }
      });

      const toolbar = quill.getModule('toolbar');
      toolbar.addHandler('background', function (value) {
        quill.format('background', value || false);
      });
      globalToolbarContainer.appendChild(toolbar.container);
      globalToolbarContainer.removeChild(toolbar.container);
    } else if (
      currentSlideId &&
      currentSlideId === checkCurrentSlideId &&
      !quillRef.current.getSelection()
    ) {
      quillRef.current.setContents(shapeData.delta, 'listener');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentMode, shapeData]);

  useEffect(() => {
    if (isSelected) {
      addResizer(document.getElementById(shapeId));
    } else {
      removeResizer(document.getElementById(shapeId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected]);

  useEffect(() => {
    const globalToolbarContainer = document.querySelector('#quillToolbar');
    if (globalToolbarContainer.children.length !== 0) {
      globalToolbarContainer.firstElementChild.remove();
    }
  }, [currentSlideId]);

  function handleShapeClick() {
    if (!presentMode) {
      setIsSelected(true);
    }
  }

  // PowerPoint's model: a click selects the shape, a double-click enters its
  // text. Until then the text layer stays inert so use-resizer resolves clicks
  // to the shape rather than giving the Quill host its own selection box.
  function handleDoubleClick() {
    if (presentMode || isPropertyPreviewMode) return;
    setIsEditingText(true);
    quillRef.current?.focus();
  }

  function handleBlur(e) {
    // Focus moving into the shape's own text counts as staying selected.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsSelected(false);
    setIsEditingText(false);
  }

  return (
    <div
      ref={containerRef}
      id={shapeId}
      tabIndex="0"
      onMouseDown={isPropertyPreviewMode || presentMode ? undefined : dragEvent}
      onBlur={isPropertyPreviewMode ? undefined : handleBlur}
      className={`${classes.circleCanvas} shape`}
      onClick={isPropertyPreviewMode || presentMode ? undefined : handleShapeClick}
      onDoubleClick={isPropertyPreviewMode || presentMode ? undefined : handleDoubleClick}
      style={{
        ...shapeData.styles,
        width: isPropertyPreviewMode ? 138 : internalProperties.width,
        height: isPropertyPreviewMode ? 138 : internalProperties.height,
        position: isPropertyPreviewMode ? 'unset' : shapeData.styles?.position,
        transform: [
          `rotate(${internalProperties.rotation}deg)`,
          `scaleX(${internalProperties.flipH ? -1 : 1})`,
          `scaleY(${internalProperties.flipV ? -1 : 1})`,
        ].join(' '),
        // Transform-based entrance effects compose this, so it carries rotation
        // alone — the animation system must not inherit the flip.
        '--ms-rot': `${internalProperties.rotation}deg`,
        cursor: isPropertyPreviewMode ? 'default' : shapeData.styles?.cursor,
        ...shapeAnim.style,
      }}
    >
      <ShapeGeometry
        shapeId={shapeId}
        preset={preset}
        adjustments={adjustments}
        size={size}
        properties={internalProperties}
      />
      <ShapeText
        shapeId={shapeId}
        textRect={textRect}
        isEditing={isEditingText && !presentMode && !isPropertyPreviewMode}
      />
      {isSelected && !presentMode && !isPropertyPreviewMode && (
        <ShapeAdjustHandles
          shapeId={shapeId}
          preset={preset}
          adjustments={adjustments}
          box={box}
          currentSlideId={currentSlideId}
        />
      )}
    </div>
  );
};

export default Shape;
